import { useEffect, useState } from "react"
import { WebMercatorViewport } from "integrations/deckgl"
import { getViewport, viewStateEventEmitter } from "features/map/GlobalViewState"

export type BBox = { north: number; south: number; east: number; west: number }

export const VIEWPORT_DEBOUNCE_MS = 750

export const bboxKey = (b: BBox): string =>
    `${b.north.toFixed(4)},${b.south.toFixed(4)},${b.east.toFixed(4)},${b.west.toFixed(4)}`

const BBOX_GRID_DEG = 1
const BBOX_PAD_FRACTION = 0.25
export const quantizeBbox = (b: BBox): BBox => {
    const padLat = (b.north - b.south) * BBOX_PAD_FRACTION
    const padLon = (b.east - b.west) * BBOX_PAD_FRACTION
    const snapDown = (v: number): number => Math.floor(v / BBOX_GRID_DEG) * BBOX_GRID_DEG
    const snapUp = (v: number): number => Math.ceil(v / BBOX_GRID_DEG) * BBOX_GRID_DEG
    return {
        west: Math.max(-180, snapDown(b.west - padLon)),
        south: Math.max(-90, snapDown(b.south - padLat)),
        east: Math.min(180, snapUp(b.east + padLon)),
        north: Math.min(90, snapUp(b.north + padLat)),
    }
}

export type SettledView = { bbox: BBox; zoom: number }

const settledViewFrom = (vp: WebMercatorViewport): SettledView | null => {
    const bounds = vp.getBounds?.()
    if (!bounds) return null
    const [west, south, east, north] = bounds
    return { bbox: quantizeBbox({ north, south, east, west }), zoom: Math.floor(vp.zoom) }
}

const sameSettledView = (a: SettledView, b: SettledView): boolean =>
    a.zoom === b.zoom && bboxKey(a.bbox) === bboxKey(b.bbox)

export const useSettledViewportBbox = (active: boolean): BBox | null => {
    const [bbox, setBbox] = useState<BBox | null>(null)

    useEffect(() => {
        if (!active) {
            setBbox(null)
            return
        }
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const onViewport = (vp: WebMercatorViewport) => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(() => {
                const bounds = vp.getBounds?.()
                if (!bounds) return
                const [west, south, east, north] = bounds
                const next = { north, south, east, west }
                setBbox((prev) => (prev && bboxKey(prev) === bboxKey(next) ? prev : next))
            }, VIEWPORT_DEBOUNCE_MS)
        }
        onViewport(getViewport())
        viewStateEventEmitter.on("updatedViewport", onViewport)
        return () => {
            clearTimeout(timeoutId)
            viewStateEventEmitter.off("updatedViewport", onViewport)
        }
    }, [active])

    return bbox
}

export const useSettledView = (active: boolean): SettledView | null => {
    const [settled, setSettled] = useState<SettledView | null>(null)

    useEffect(() => {
        if (!active) {
            setSettled(null)
            return
        }
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const onViewport = (vp: WebMercatorViewport) => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(() => {
                const next = settledViewFrom(vp)
                if (!next) return
                setSettled((prev) => (prev && sameSettledView(prev, next) ? prev : next))
            }, VIEWPORT_DEBOUNCE_MS)
        }
        onViewport(getViewport())
        viewStateEventEmitter.on("updatedViewport", onViewport)
        return () => {
            clearTimeout(timeoutId)
            viewStateEventEmitter.off("updatedViewport", onViewport)
        }
    }, [active])

    return settled
}
